import RenderRichTextContent from '@components/common/render-rich-text-content'
import { OPTION_BUTTON_BG_COLORS } from '@lib/constants/quizzes'
import { QuizProblemType } from '@lib/types/quizzes'
import { Dispatch, SetStateAction } from 'react'
import OptionButton from '@components/quizzes/arena/problems/option-button'
import classNames from 'classnames'

type Props = {
  problem: QuizProblemType
  isHidden: boolean
  isValidating: boolean
  onSubmit: (choiceIds: number[]) => Promise<void>
  selectedChoiceIds: number[]
  setSelectedChoiceIds: Dispatch<SetStateAction<number[]>>
}

const Problem = ({
  problem,
  isHidden,
  isValidating,
  onSubmit,
  selectedChoiceIds,
  setSelectedChoiceIds,
}: Props) => {
  const handleOptionClick = (id: number) => {
    if (isValidating || problem.isAnswerValidated) return
    if (problem.problemType === 'Multiple Choice') {
      setSelectedChoiceIds([id])
      onSubmit([id])
    } else {
      selectedChoiceIds.find((selectedChoiceId) => selectedChoiceId === id)
        ? setSelectedChoiceIds(
            selectedChoiceIds.filter((optionId) => optionId !== id)
          )
        : setSelectedChoiceIds([...selectedChoiceIds, id])
    }
  }

  const optionButtonsContainerClasses = classNames(
    'row-span-2 lg:row-span-1 grid gap-2',
    {
      'grid-rows-2 lg:grid-cols-2 lg:grid-rows-1':
        problem?.choices.length === 2,
      'grid-rows-3 lg:grid-cols-3 lg:grid-rows-1':
        problem?.choices.length === 3,
      'grid-rows-4 lg:grid-cols-4 lg:grid-rows-1':
        problem?.choices.length === 4,
      'grid-rows-5 lg:grid-cols-5 lg:grid-rows-1':
        problem?.choices.length === 5,
      'grid-rows-6 lg:grid-cols-6 lg:grid-rows-1':
        problem?.choices.length === 6,
    }
  )

  return (
    <div
      className={classNames('grid h-full mx-5 gap-3 duration-500', {
        'opacity-100': !isHidden,
        'opacity-0': isHidden,
      })}
      style={{ gridAutoRows: '1fr', gridTemplateRows: '49% 49%' }}
    >
      <div className="flex items-center justify-center bg-black bg-opacity-50 rounded-lg overflow-auto thin-scroll-bar">
        <RenderRichTextContent
          content={problem?.description}
          className="font-600 leading-tight text-xl lg:text-4xl p-5 max-h-full"
        />
      </div>
      <div className={optionButtonsContainerClasses}>
        {problem?.choices.map((choice, index) => (
          <OptionButton
            key={choice.id}
            buttonBgColor={OPTION_BUTTON_BG_COLORS[index]}
            content={choice.choice}
            keyupTriggerKey={`${index + 1}`}
            onClick={() => handleOptionClick(choice.id)}
            isSelected={
              !!selectedChoiceIds.find(
                (selectedChoiceId) => selectedChoiceId === choice.id
              )
            }
            isLoading={isValidating}
            isAnswerValidated={problem?.isAnswerValidated}
            isCorrect={
              !!problem?.correctChoices?.find(
                (correctChoiceId) => correctChoiceId === choice.id
              )
            }
            problemType={problem?.problemType}
          />
        ))}
      </div>
    </div>
  )
}

export default Problem
